import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "dropdown", "selectedList", "hiddenInputs"]
  static values = {
    available: Array,
    selected: Array
  }

  connect() {
    this.renderSelected()
    document.addEventListener("click", this.closeDropdown)
  }

  disconnect() {
    document.removeEventListener("click", this.closeDropdown)
  }

  closeDropdown = (event) => {
    if (!this.element.contains(event.target)) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  focus() {
    this.filter()
    this.dropdownTarget.classList.remove("hidden")
  }

  filter() {
    const query = this.inputTarget.value.toLowerCase().trim()
    const selectedIds = new Set(this.selectedValue.map(t => t.id))

    const filtered = this.availableValue.filter(tag =>
      !selectedIds.has(tag.id) && tag.name.toLowerCase().includes(query)
    )

    let html = filtered.map(tag =>
      `<button type="button" data-action="tag-picker#select" data-tag-id="${tag.id}" data-tag-name="${tag.name}"
        class="block w-full text-left px-3 py-2 text-sm text-[var(--color-ink)] hover:bg-[var(--color-paper-sunken)] transition-colors">
        ${this.escapeHtml(tag.name)}
      </button>`
    ).join("")

    if (query.length > 0 && !this.availableValue.some(t => t.name.toLowerCase() === query)) {
      html += `<button type="button" data-action="tag-picker#create" data-tag-name="${this.escapeHtml(query)}"
        class="block w-full text-left px-3 py-2 text-sm text-[var(--color-accent)] hover:bg-[var(--color-paper-sunken)] transition-colors font-medium">
        + Create "${this.escapeHtml(query)}"
      </button>`
    }

    if (html === "") {
      html = `<div class="px-3 py-2 text-sm text-[var(--color-ink-tertiary)]">Type to search or create a tag</div>`
    }

    this.dropdownTarget.innerHTML = html
    this.dropdownTarget.classList.remove("hidden")
  }

  select(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.tagId
    const name = event.currentTarget.dataset.tagName
    const selected = [...this.selectedValue, { id, name }]
    this.selectedValue = selected
    this.inputTarget.value = ""
    this.dropdownTarget.classList.add("hidden")
    this.renderSelected()
  }

  async create(event) {
    event.preventDefault()
    const name = event.currentTarget.dataset.tagName

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch("/tags", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "Accept": "application/json"
        },
        body: JSON.stringify({ tag: { name } })
      })

      if (response.ok) {
        const tag = await response.json()
        // Remove any stale entry with the same id (e.g. revived tag) before adding
        this.availableValue = [
          ...this.availableValue.filter(t => t.id !== tag.id),
          { id: tag.id, name: tag.name }
        ]
        this.selectedValue = [...this.selectedValue, { id: tag.id, name: tag.name }]
        this.inputTarget.value = ""
        this.dropdownTarget.classList.add("hidden")
        this.renderSelected()
      } else {
        const body = await response.json().catch(() => null)
        const message = body?.errors?.join(", ") || "Could not create tag"
        this.dropdownTarget.innerHTML = `<div class="px-3 py-2 text-sm text-[var(--color-danger)]">${this.escapeHtml(message)}</div>`
      }
    } catch (error) {
      console.error("Failed to create tag:", error)
      this.dropdownTarget.innerHTML = `<div class="px-3 py-2 text-sm text-[var(--color-danger)]">Network error — could not create tag</div>`
    }
  }

  remove(event) {
    event.preventDefault()
    const id = event.currentTarget.dataset.tagId
    this.selectedValue = this.selectedValue.filter(t => t.id !== id)
    this.renderSelected()
  }

  renderSelected() {
    // Render pills
    this.selectedListTarget.innerHTML = this.selectedValue.map(tag =>
      `<span class="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-[var(--color-accent-light)] text-[var(--color-accent-text)] text-xs font-medium">
        ${this.escapeHtml(tag.name)}
        <button type="button" data-action="tag-picker#remove" data-tag-id="${tag.id}" class="hover:text-red-500 transition-colors">&times;</button>
      </span>`
    ).join("")

    // Render hidden inputs
    this.hiddenInputsTarget.innerHTML = this.selectedValue.map(tag =>
      `<input type="hidden" name="tag_ids[]" value="${tag.id}">`
    ).join("")
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
